import { useState, useEffect, useMemo, Fragment } from "react";
import { useI18n } from "@/i18n/I18nProvider";

/**
 * FAQ — 10 domande complete, bilingue via translations.js.
 * Le risposte usano una mini sintassi markdown-lite:
 *   **bold** → <strong>bold</strong>
 *   *italic* → <em>italic</em>
 *   \n\n → nuovo paragrafo (<br /><br />)
 * Manteniamo <strong> per l'accessibilità + Schema.org FAQPage JSON-LD.
 */

const FAQ_KEYS = [
    { q: "faq.q1.q", a: "faq.q1.a" },
    { q: "faq.q2.q", a: "faq.q2.a" },
    { q: "faq.q3.q", a: "faq.q3.a" },
    { q: "faq.q4.q", a: "faq.q4.a" },
    { q: "faq.q5.q", a: "faq.q5.a" },
    { q: "faq.q6.q", a: "faq.q6.a" },
    { q: "faq.q7.q", a: "faq.q7.a" },
    { q: "faq.q8.q", a: "faq.q8.a" },
    { q: "faq.q9.q", a: "faq.q9.a" },
    { q: "faq.q10.q", a: "faq.q10.a" },
];

/**
 * Renders markdown-lite (**strong**, *em*, \n\n paragraphs).
 * Enough for our FAQ needs without pulling in a real markdown lib.
 */
function renderRich(text) {
    const parts = text.split(/\n\n+/);
    return parts.map((para, pIdx) => (
        <Fragment key={para}>
            {pIdx > 0 && <><br /><br /></>}
            {renderInline(para)}
        </Fragment>
    ));
}
function renderInline(str) {
    const tokens = [];
    let rest = str;
    let key = 0;
    const re = /(\*\*[^*]+\*\*|\*[^*]+\*)/;
    while (rest.length) {
        const m = rest.match(re);
        if (!m) { tokens.push(rest); break; }
        if (m.index > 0) tokens.push(rest.slice(0, m.index));
        const token = m[0];
        if (token.startsWith("**")) {
            tokens.push(<strong key={`s-${key++}`}>{token.slice(2, -2)}</strong>);
        } else {
            tokens.push(<em key={`e-${key++}`}>{token.slice(1, -1)}</em>);
        }
        rest = rest.slice(m.index + token.length);
    }
    return tokens;
}

function plainText(text) {
    // Strip markdown-lite for Schema.org JSON-LD (needs pure text).
    return text.replace(/\*\*/g, "").replace(/\*/g, "").replace(/\n\n+/g, " ");
}

/* Injects the FAQPage JSON-LD schema into <head>, replacing it on language change. */
function useFaqSchema(faqs) {
    useEffect(() => {
        const schema = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((f) => ({
                "@type": "Question",
                "name": f.q,
                "acceptedAnswer": { "@type": "Answer", "text": plainText(f.a) },
            })),
        };
        const el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = "itly-faq-schema";
        el.textContent = JSON.stringify(schema);
        document.getElementById("itly-faq-schema")?.remove();
        document.head.appendChild(el);
        return () => document.getElementById("itly-faq-schema")?.remove();
    }, [faqs]);
}

function FAQIntro({ t }) {
    return (
        <div>
            <p
                style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 12.5,
                    fontWeight: 700,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "var(--itly-orange-700)",
                    margin: 0,
                }}
            >
                {t("faq.badge")}
            </p>
            <h2
                className="itly-serif"
                style={{
                    marginTop: 14,
                    marginBottom: 18,
                    fontSize: "clamp(30px, 3.6vw, 46px)",
                    lineHeight: 1.08,
                    color: "var(--itly-fg-strong)",
                    letterSpacing: "-0.022em",
                }}
            >
                {t("faq.title_a")} <em style={{ fontStyle: "italic", color: "var(--itly-orange-500)", fontWeight: 500 }}>{t("faq.title_b")}</em> {t("faq.title_c")}
            </h2>
            <p
                style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 15,
                    lineHeight: 1.6,
                    color: "var(--itly-muted)",
                    maxWidth: 320,
                }}
            >
                {t("faq.helpText")}{" "}
                <a
                    href="mailto:info.itly9@gmail.com"
                    style={{
                        color: "var(--itly-fg-strong)",
                        textDecoration: "underline",
                        textUnderlineOffset: 3,
                    }}
                >
                    info.itly9@gmail.com
                </a>
                .
            </p>
        </div>
    );
}

export default function FAQ() {
    const { t } = useI18n();
    const [open, setOpen] = useState(0);

    const faqs = useMemo(() => FAQ_KEYS.map((f) => ({ id: f.q, q: t(f.q), a: t(f.a) })), [t]);

    useFaqSchema(faqs);

    return (
        <section
            id="faq"
            data-testid="faq-section"
            style={{
                background: "var(--itly-page)",
                borderTop: "1px solid var(--itly-border)",
                padding: "96px 32px",
                position: "relative",
                overflow: "hidden",
            }}
            data-reveal="up"
        >

            <div
                style={{
                    position: "relative",
                    zIndex: 1,
                    maxWidth: 920,
                    margin: "0 auto",
                    display: "grid",
                    gridTemplateColumns: "1fr 1.4fr",
                    gap: 56,
                    alignItems: "start",
                }}
                className="itly-adaptive-wrap"
            >
                <FAQIntro t={t} />

                <div data-testid="faq-list" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {faqs.map((f, i) => (
                        <FAQItem
                            key={f.id}
                            index={i}
                            question={f.q}
                            answer={f.a}
                            isOpen={open === i}
                            onToggle={() => setOpen(open === i ? -1 : i)}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}

function FAQItem({ index, question, answer, isOpen, onToggle }) {
    return (
        <div
            data-testid={`faq-item-${index}`}
            style={{
                background: "var(--itly-surface)",
                border: "1px solid var(--itly-border)",
                borderRadius: 14,
                boxShadow: isOpen ? "var(--itly-card-shadow)" : "none",
                transition: "box-shadow 220ms ease",
                overflow: "hidden",
            }}
        >
            <button
                type="button"
                onClick={onToggle}
                data-testid={`faq-toggle-${index}`}
                aria-expanded={isOpen}
                style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 14,
                    padding: "18px 20px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 15,
                    fontWeight: 600,
                    color: "var(--itly-fg-strong)",
                    letterSpacing: "-0.005em",
                }}
            >
                <span style={{ flex: 1 }}>{question}</span>
                <span
                    aria-hidden="true"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        background: isOpen ? "var(--itly-orange-500)" : "var(--itly-teal-050)",
                        color: isOpen ? "#fff" : "var(--itly-fg-strong)",
                        flexShrink: 0,
                        transition: "background 220ms ease, transform 220ms ease",
                        transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14" />
                    </svg>
                </span>
            </button>
            <div
                style={{
                    maxHeight: isOpen ? 620 : 0,
                    overflow: "hidden",
                    transition: "max-height 320ms cubic-bezier(0.2, 0.7, 0.2, 1)",
                }}
                data-testid={`faq-content-${index}`}
            >
                <div
                    style={{
                        padding: "0 20px 20px",
                        fontFamily: "Inter, sans-serif",
                        fontSize: 14.5,
                        lineHeight: 1.65,
                        color: "var(--itly-muted)",
                    }}
                >
                    {renderRich(answer)}
                </div>
            </div>
        </div>
    );
}
